<template>
  <div class="page">
    <div class="head">
      <div>
        <h2>WhatsApp Templates</h2>
        <p>Sync approved WhatsApp templates from Meta and use them in broadcasts.</p>
      </div>

      <button class="btn primary" :disabled="syncing" @click="syncTemplates">
        {{ syncing ? "Syncing..." : "🔄 Sync Templates" }}
      </button>
    </div>

    <div class="toolbar">
      <input v-model="search" placeholder="Search template..." />

      <select v-model="statusFilter">
        <option value="">All Status</option>
        <option value="APPROVED">APPROVED</option>
        <option value="PENDING">PENDING</option>
        <option value="REJECTED">REJECTED</option>
      </select>

      <button class="btn" @click="loadTemplates">Refresh</button>
    </div>

    <div class="layout">
      <!-- LEFT TABLE -->
      <div class="card table-card">
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Template</th>
              <th>Language</th>
              <th>Status</th>
              <th>Header</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="t in paginatedTemplates"
              :key="t.id"
              :class="{ active: selectedTemplate?.id === t.id }"
              @click="openTemplate(t)"
            >
              <td>#{{ t.id }}</td>

              <td>
                <b>{{ t.name }}</b>
                <div class="muted">{{ t.category || "-" }}</div>
                <div class="body-small">{{ t.body_text || "-" }}</div>
              </td>

              <td>{{ t.language_code }}</td>

              <td>
                <span class="badge" :class="t.status">
                  {{ t.status || "-" }}
                </span>
              </td>

              <td>{{ t.header_type || "NONE" }}</td>
              <td>{{ formatDate(t.created_at) }}</td>

              <td>
                <button class="btn" @click.stop="openTemplate(t)">
                  Open
                </button>
              </td>
            </tr>

            <tr v-if="!filteredTemplates.length">
              <td colspan="7" class="empty">No templates found</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination">
          <div class="page-info">
            Showing {{ paginationStart }} - {{ paginationEnd }} of
            {{ filteredTemplates.length }}
          </div>

          <div class="page-actions">
            <select v-model.number="pageSize">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>

            <button class="btn" :disabled="currentPage === 1" @click="currentPage--">
              Prev
            </button>

            <span>Page {{ currentPage }} / {{ totalPages }}</span>

            <button
              class="btn"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- RIGHT PREVIEW -->
      <div class="card preview-panel">
        <div v-if="selectedTemplate">
          <div class="preview-head">
            <div>
              <h3>{{ selectedTemplate.name }}</h3>
              <p>
                {{ selectedTemplate.language_code }} •
                {{ selectedTemplate.status }} •
                {{ selectedTemplate.category || "-" }}
              </p>
            </div>

            <span class="badge" :class="selectedTemplate.status">
              {{ selectedTemplate.status }}
            </span>
          </div>

          <div class="meta-grid">
            <div>
              <label>Header</label>
              <strong>{{ selectedTemplate.header_type || "NONE" }}</strong>
            </div>

            <div>
              <label>Language</label>
              <strong>{{ selectedTemplate.language_code || "-" }}</strong>
            </div>

            <div>
              <label>Created</label>
              <strong>{{ formatDate(selectedTemplate.created_at) }}</strong>
            </div>

            <div>
              <label>Category</label>
              <strong>{{ selectedTemplate.category || "-" }}</strong>
            </div>
          </div>

          <div class="wa-preview">
            <div class="wa-card">
              <div
                v-if="selectedTemplate.header_type === 'IMAGE'"
                class="preview-header"
              >
                <img src="https://via.placeholder.com/420x180" />
              </div>

              <div
                v-else-if="selectedTemplate.header_text"
                class="wa-header-text"
              >
                {{ formatTemplate(selectedTemplate.header_text) }}
              </div>

              <div class="preview-body">
                {{ formatTemplate(selectedTemplate.body_text) || "-" }}
              </div>

              <div v-if="selectedTemplate.footer_text" class="preview-footer">
                {{ selectedTemplate.footer_text }}
              </div>

              <div
                v-if="parsedButtons.length"
                class="preview-buttons"
              >
                <button
                  v-for="(b, i) in parsedButtons"
                  :key="i"
                  class="btn-outline"
                >
                  {{ b.text || b.title || "Button" }}
                </button>
              </div>
            </div>
          </div>

          <div class="template-json">
            <h4>Body Text</h4>
            <pre>{{ selectedTemplate.body_text || "-" }}</pre>
          </div>
        </div>

        <div v-else class="empty-preview">
          Select a template from the table to preview it here.
        </div>
      </div>
    </div>

    <div v-if="message" class="toast">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import VARS from "@/services/vars";
import { auth } from "@/services/auth";

const API = VARS.API_URL;

const templates = ref([]);
const search = ref("");
const statusFilter = ref("");
const syncing = ref(false);
const message = ref("");

const selectedTemplate = ref(null);

const currentPage = ref(1);
const pageSize = ref(10);

function openTemplate(t) {
  selectedTemplate.value = t;
}

function formatTemplate(text) {
  if (!text) return "";

  return String(text).replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    return `[${n}]`;
  });
}

const parsedButtons = computed(() => {
  const buttons = selectedTemplate.value?.buttons;

  if (!buttons) return [];

  if (Array.isArray(buttons)) return buttons;

  try {
    return JSON.parse(buttons);
  } catch {
    return [];
  }
});

const filteredTemplates = computed(() => {
  const q = search.value.toLowerCase().trim();

  return templates.value.filter((t) => {
    const matchesStatus =
      !statusFilter.value || t.status === statusFilter.value;

    const matchesSearch =
      !q ||
      String(t.name || "").toLowerCase().includes(q) ||
      String(t.body_text || "").toLowerCase().includes(q) ||
      String(t.language_code || "").toLowerCase().includes(q) ||
      String(t.category || "").toLowerCase().includes(q);

    return matchesStatus && matchesSearch;
  });
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(filteredTemplates.value.length / pageSize.value));
});

const paginatedTemplates = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredTemplates.value.slice(start, start + pageSize.value);
});

const paginationStart = computed(() => {
  if (!filteredTemplates.value.length) return 0;
  return (currentPage.value - 1) * pageSize.value + 1;
});

const paginationEnd = computed(() => {
  return Math.min(
    currentPage.value * pageSize.value,
    filteredTemplates.value.length
  );
});

watch([search, statusFilter, pageSize], () => {
  currentPage.value = 1;
});

watch(filteredTemplates, () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = totalPages.value;
  }
});

function headers() {
  return {
    Authorization: "Bearer " + auth.token,
  };
}

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleString();
}

function showToast(text) {
  message.value = text;
  setTimeout(() => {
    message.value = "";
  }, 2500);
}

async function loadTemplates() {
  try {
    const res = await fetch(`${API}/api/manager/wa-templates`, {
      headers: headers(),
    });

    const data = await res.json();

    if (!data.ok) {
      showToast(data.message || "Failed to load templates");
      return;
    }

    templates.value = data.data || [];

    if (!selectedTemplate.value && templates.value.length) {
      selectedTemplate.value = templates.value[0];
    }
  } catch (err) {
    console.error(err);
    showToast("Failed to load templates");
  }
}

async function syncTemplates() {
  try {
    syncing.value = true;

    const res = await fetch(`${API}/api/manager/wa-templates/sync`, {
      headers: headers(),
    });

    const data = await res.json();

    if (!data.ok) {
      showToast(data.message || "Template sync failed");
      return;
    }

    showToast(`Synced ${data.count || 0} templates`);
    await loadTemplates();
  } catch (err) {
    console.error(err);
    showToast("Template sync failed");
  } finally {
    syncing.value = false;
  }
}

onMounted(() => {
  loadTemplates();
});
</script>

<style scoped>
.page {
  padding: 18px;
  min-height: 100vh;
  background: #f8fafc;
}

.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.head h2 {
  margin: 0;
  color: #0f172a;
}

.head p {
  margin: 4px 0 0;
  color: #64748b;
}

.toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

input,
select {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 9px 10px;
  background: #fff;
}

.btn {
  border: none;
  border-radius: 8px;
  padding: 9px 13px;
  background: #e2e8f0;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
}

.btn.small {
  padding: 6px 10px;
  font-size: 12px;
}

.btn.primary {
  background: #2563eb;
  color: #fff;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(380px, 0.8fr);
  gap: 16px;
  align-items: start;
}

.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.table-card {
  overflow: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background: #f1f5f9;
  padding: 10px;
  text-align: left;
  color: #334155;
  font-size: 12px;
  white-space: nowrap;
}

.table td {
  padding: 10px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  vertical-align: top;
}

.table tbody tr {
  cursor: pointer;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.table tbody tr.active {
  background: #eff6ff;
}

.body-small {
  max-width: 360px;
  margin-top: 6px;
  color: #475569;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.muted {
  color: #64748b;
  font-size: 12px;
  margin-top: 4px;
}

.badge {
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  background: #e2e8f0;
  display: inline-block;
}

.badge.APPROVED {
  background: #dcfce7;
  color: #15803d;
}

.badge.PENDING {
  background: #fef3c7;
  color: #92400e;
}

.badge.REJECTED {
  background: #fee2e2;
  color: #b91c1c;
}

.empty {
  text-align: center;
  color: #64748b;
  padding: 24px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  flex-wrap: wrap;
}

.page-info {
  color: #64748b;
  font-size: 13px;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-actions select {
  margin: 0;
  padding: 7px;
}

.preview-panel {
  padding: 16px;
  position: sticky;
  top: 16px;
}

.preview-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.preview-head h3 {
  margin: 0;
  color: #0f172a;
}

.preview-head p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 13px;
}

.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.meta-grid div {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 10px;
  border-radius: 10px;
}

.meta-grid label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.meta-grid strong {
  color: #0f172a;
  font-size: 13px;
}

.wa-preview {
  background: #e5ddd5;
  border-radius: 14px;
  padding: 18px;
}

.wa-card {
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  max-width: 420px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
}

.preview-header img {
  width: 100%;
  border-radius: 10px;
  display: block;
  margin-bottom: 10px;
}

.wa-header-text {
  font-weight: 800;
  margin-bottom: 8px;
  color: #0f172a;
}

.preview-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  color: #0f172a;
}

.preview-footer {
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
}

.preview-buttons {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}

.btn-outline {
  border: 1px solid #2563eb;
  color: #2563eb;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  font-weight: 700;
}

.template-json {
  margin-top: 16px;
}

.template-json h4 {
  margin: 0 0 8px;
}

.template-json pre {
  margin: 0;
  white-space: pre-wrap;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  font-size: 12px;
  color: #334155;
}

.empty-preview {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  text-align: center;
}

.toast {
  position: fixed;
  right: 20px;
  bottom: 20px;
  background: #0f172a;
  color: #fff;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.25);
}

@media (max-width: 1100px) {
  .layout {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: static;
  }
}

@media (max-width: 760px) {
  .head,
  .toolbar,
  .page-actions,
  .pagination {
    flex-direction: column;
    align-items: stretch;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>